import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../lib/api";

export const Route = createFileRoute("/sessions/$slug/transcript")({
  component: TranscriptPage,
});

interface Line {
  tc?: string; // mm:ss or hh:mm:ss
  text: string;
}

function parseTranscript(md: string): Line[] {
  return md.split("\n").map((raw) => {
    const m = raw.match(/^\[(\d{1,2}:\d{2}(?::\d{2})?)\]\s*(.*)$/);
    if (m && m[1] && m[2] !== undefined) return { tc: m[1], text: m[2] };
    return { text: raw };
  });
}

function tcToSeconds(tc: string): number {
  const parts = tc.split(":").map(Number);
  if (parts.length === 3) return parts[0]! * 3600 + parts[1]! * 60 + parts[2]!;
  if (parts.length === 2) return parts[0]! * 60 + parts[1]!;
  return 0;
}

function TranscriptPage() {
  const { t } = useTranslation();
  const { slug } = Route.useParams();
  const report = useQuery({ queryKey: ["session", slug], queryFn: () => api.getReport(slug) });
  const tx = useQuery({ queryKey: ["transcript", slug], queryFn: () => api.getTranscript(slug) });
  const [seek, setSeek] = useState<number | null>(null);

  const lines = useMemo(() => (tx.data ? parseTranscript(tx.data) : []), [tx.data]);

  // YouTube videoId extraction (rough — only youtube.com/youtu.be)
  const videoId = useMemo(() => {
    const url = report.data?.input ?? "";
    const m = url.match(/(?:v=|youtu\.be\/|embed\/|shorts\/)([\w-]{11})/);
    return m ? m[1] : null;
  }, [report.data]);

  return (
    <div className="mx-auto grid max-w-[1320px] grid-cols-1 gap-6 px-6 py-10 md:grid-cols-[1fr_400px]">
      <article className="space-y-2">
        <Link to="/sessions/$slug" params={{ slug }} className="text-sm text-a2 hover:underline">← {t("common.report")}</Link>
        <h1 className="font-heading text-2xl">{t("transcript.title")}</h1>
        {tx.isLoading && <p className="text-ink/50">{t("common.loading")}</p>}
        {tx.error && <p className="text-a1">{t("transcript.notAvailable")}</p>}
        <div className="font-body text-sm leading-relaxed">
          {lines.map((ln, i) => (
            <p key={i} className="py-0.5">
              {ln.tc && (
                <button
                  className="mr-2 font-mono text-xs text-a2 hover:underline"
                  onClick={() => setSeek(tcToSeconds(ln.tc!))}
                >
                  [{ln.tc}]
                </button>
              )}
              {ln.text}
            </p>
          ))}
        </div>
      </article>

      <aside className="md:sticky md:top-4 md:self-start">
        {videoId ? (
          <div className="aspect-video w-full overflow-hidden rounded">
            <iframe
              key={seek ?? "init"}
              className="h-full w-full"
              src={`https://www.youtube.com/embed/${videoId}${seek != null ? `?start=${seek}&autoplay=1` : ""}`}
              allow="autoplay; encrypted-media"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
              title="video"
            />
          </div>
        ) : (
          <div className="re-card text-sm text-ink/50">{t("transcript.noEmbed")}</div>
        )}
        {seek != null && (
          <p className="mt-2 text-xs text-ink/40">{t("transcript.jumpTo", { sec: seek })}</p>
        )}
      </aside>
    </div>
  );
}
